import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CLASSIFICACOES = [
  'Terreno',
  'Residência',
  'Comercial',
  'Apartamento',
  'Sala',
  'Sítio',
  'Prédio',
  'Chácara'
];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR');
};

function ImoveisTemplate({ getAuthHeaders }) {
  const [imoveis, setImoveis] = useState([]);
  const [stats, setStats] = useState(null);
  const [showNovoImovel, setShowNovoImovel] = useState(false);
  const [showVendaImovel, setShowVendaImovel] = useState(false);
  const [editingImovel, setEditingImovel] = useState(null);
  const [tabelaExpandida, setTabelaExpandida] = useState(true);

  useEffect(() => {
    carregarDados();
  }, []);

  const carregarDados = async () => {
    try {
      const [imoveisRes, statsRes] = await Promise.all([
        axios.get(`${API}/imoveis`, getAuthHeaders()),
        axios.get(`${API}/imoveis/estatisticas`, getAuthHeaders())
      ]);
      
      setImoveis(imoveisRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    }
  };

  const handleNovoImovel = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      endereco: formData.get('endereco'),
      data_cadastro: formData.get('data_cadastro'),
      valor: parseFloat(formData.get('valor')),
      classificacao: formData.get('classificacao')
    };

    try {
      if (editingImovel) {
        await axios.put(`${API}/imoveis/${editingImovel.id}`, data, getAuthHeaders());
        toast.success('Imóvel atualizado');
      } else {
        await axios.post(`${API}/imoveis`, data, getAuthHeaders());
        toast.success('Imóvel cadastrado');
      }
      setShowNovoImovel(false);
      setEditingImovel(null);
      carregarDados();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const handleVendaImovel = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const data = {
      valor_venda: parseFloat(formData.get('valor_venda')),
      data_venda: formData.get('data_venda')
    };

    const imovelId = formData.get('imovel_id');

    try {
      await axios.post(`${API}/imoveis/${imovelId}/vender`, data, getAuthHeaders());
      toast.success('Venda registrada');
      setShowVendaImovel(false);
      carregarDados();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao registrar venda');
    }
  };

  const handleExcluir = async (id) => {
    if (!window.confirm('Deseja excluir este imóvel?')) return;
    try {
      await axios.delete(`${API}/imoveis/${id}`, getAuthHeaders());
      toast.success('Imóvel excluído');
      carregarDados();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleEditar = (imovel) => {
    setEditingImovel(imovel);
    setShowNovoImovel(true);
  };

  return (
    <div>
      {/* Cards de Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-100">
                <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total em Imóveis</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_imoveis)}</p>
                <p className="text-xs text-gray-500 mt-1">{stats.quantidade_imoveis} imóve{stats.quantidade_imoveis !== 1 ? 'is' : 'l'} cadastrado{stats.quantidade_imoveis !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Vendas</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.total_vendas)}</p>
                <p className="text-xs text-gray-500 mt-1">{stats.quantidade_vendidos} imóve{stats.quantidade_vendidos !== 1 ? 'is' : 'l'} vendido{stats.quantidade_vendidos !== 1 ? 's' : ''}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botões */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => {
              setEditingImovel(null);
              setShowNovoImovel(true);
            }}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-lg hover:from-amber-700 hover:to-orange-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Novo Imóvel
          </button>

          <button
            onClick={() => setShowVendaImovel(true)}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 flex items-center justify-center gap-2 shadow-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Venda de Imóvel
          </button>
        </div>
      </div>

      {/* Tabela de Imóveis */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">Lista de Imóveis</h3>
          <button
            onClick={() => setTabelaExpandida(!tabelaExpandida)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {tabelaExpandida ? (
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            )}
          </button>
        </div>

        {tabelaExpandida && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Código</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data Cadastro/Venda</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Endereço</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classificação</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Valor Venda</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {imoveis.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      Nenhum imóvel cadastrado
                    </td>
                  </tr>
                ) : (
                  imoveis.map((imovel) => (
                    <tr key={imovel.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">{imovel.codigo}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {formatDate(imovel.data_cadastro)}
                        {imovel.data_venda && <> – {formatDate(imovel.data_venda)}</>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{imovel.endereco}</td>
                      <td className="px-4 py-3 text-sm font-medium">{formatCurrency(imovel.valor)}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{imovel.classificacao}</td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          imovel.status === 'Disponível' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {imovel.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-green-600">
                        {imovel.valor_venda ? formatCurrency(imovel.valor_venda) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditar(imovel)}
                            className="text-indigo-600 hover:text-indigo-900 p-1"
                            title="Editar"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleExcluir(imovel.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Excluir"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Novo Imóvel */}
      {showNovoImovel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => {
          setShowNovoImovel(false);
          setEditingImovel(null);
        }}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">{editingImovel ? 'Editar Imóvel' : 'Novo Imóvel'}</h2>
                <button onClick={() => {
                  setShowNovoImovel(false);
                  setEditingImovel(null);
                }} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleNovoImovel} className="p-6">
              <div className="space-y-4">
                {!editingImovel && (
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Código:</strong> Será gerado automaticamente
                    </p>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Endereço do Imóvel *</label>
                  <input
                    type="text"
                    name="endereco"
                    required
                    defaultValue={editingImovel?.endereco}
                    placeholder="Rua, número, bairro, cidade"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data do Cadastro *</label>
                  <input
                    type="date"
                    name="data_cadastro"
                    required
                    defaultValue={editingImovel?.data_cadastro || new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor do Imóvel (R$) *</label>
                  <input
                    type="number"
                    name="valor"
                    step="0.01"
                    min="0"
                    required
                    defaultValue={editingImovel?.valor}
                    placeholder="Ex: 250000.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Classificação *</label>
                  <select
                    name="classificacao"
                    required
                    defaultValue={editingImovel?.classificacao || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Selecione...</option>
                    {CLASSIFICACOES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowNovoImovel(false);
                    setEditingImovel(null);
                  }}
                  className="flex-1 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
                >
                  {editingImovel ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Venda de Imóvel */}
      {showVendaImovel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowVendaImovel(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">Venda de Imóvel</h2>
                <button onClick={() => setShowVendaImovel(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleVendaImovel} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Selecionar Imóvel *</label>
                  <select
                    name="imovel_id"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">Selecione um imóvel...</option>
                    {imoveis.filter(i => i.status === 'Disponível').map(imovel => (
                      <option key={imovel.id} value={imovel.id}>
                        {imovel.codigo} - {imovel.endereco}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor da Venda (R$) *</label>
                  <input
                    type="number"
                    name="valor_venda"
                    step="0.01"
                    min="0"
                    required
                    placeholder="Ex: 280000.00"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data da Venda *</label>
                  <input
                    type="date"
                    name="data_venda"
                    required
                    defaultValue={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowVendaImovel(false)}
                  className="flex-1 px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  Registrar Venda
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImoveisTemplate;
